import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import SEO from '../../components/SEO';
import { useLanguage } from '../../context/LanguageContext';

const AIRBNB_URL = 'https://www.airbnb.fr/rooms/1645623046019220232?photo_id=2559907001&source_impression_id=p3_1775408408_P3OPZ0BghlFK9E6s&previous_page_section_name=1000';

const IMAGES = [
  '/photo-site/epidote/9DB5A578-7FBF-4B5C-9815-D4EB42BD7CB5.JPG',
  '/photo-site/epidote/B3C59C9C-7865-41C8-A77A-6296686E6C13.JPG',
  '/photo-site/epidote/CBDA58AA-E919-4EB7-B226-4E650B8C53A3.JPG',
  '/photo-site/epidote/EA2CE42F-BC9B-4964-BE78-C6EB78548F8F.JPG',
  '/photo-site/epidote/F4CB029E-D741-46C1-B369-B7EB07E8C188.JPG',
  '/photo-site/epidote/IMG_6862.jpeg',
  '/photo-site/epidote/IMG_7045.jpeg',
  '/photo-site/epidote/IMG_7071.jpeg',
  '/photo-site/epidote/IMG_7077.jpeg',
  '/photo-site/epidote/IMG_7078.jpeg',
  '/photo-site/epidote/IMG_7088.jpeg',
  '/photo-site/epidote/IMG_7240.jpeg',
  '/photo-site/epidote/IMG_7244.jpeg',
  '/photo-site/epidote/IMG_7245.jpeg',
  '/photo-site/epidote/IMG_7246.jpeg',
  '/photo-site/epidote/IMG_7251.jpeg',
  '/photo-site/epidote/IMG_7253.jpeg',
  '/photo-site/epidote/IMG_7254.jpeg',
  '/photo-site/epidote/IMG_7255.jpeg',
];

const AppartementEpidote = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [mainImage, setMainImage] = useState(IMAGES[0]);
  const equipements = t('epidote.equip');

  return (
    <>
    <SEO
      title={t('seo.epidote.title')}
      description={t('seo.epidote.description')}
      keywords="appartement épidote la grave, location 6 personnes la grave, grand duplex balcon famille, montagne oisans hautes-alpes"
      url="/appartements/appartement-6-personnes-epidote"
      image="https://marmottejoyeuse.fr/photo-site/epidote/9DB5A578-7FBF-4B5C-9815-D4EB42BD7CB5.JPG"
    />
    <div className="container mx-auto px-4 pt-28 pb-16 min-h-screen">
      <button
        onClick={() => navigate('/appartements')}
        className="fixed top-24 left-4 md:left-8 z-10 btn btn-circle btn-ghost bg-base-200/50 backdrop-blur-sm hover:bg-base-200"
        aria-label={t('epidote.back')}
      >
        ←
      </button>

      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <span className="text-primary font-semibold text-sm">{t('epidote.label')}</span>
          <h1 className="text-4xl font-bold mt-1">{t('epidote.name')}</h1>
          <p className="text-base-content/60 mt-1">{t('epidote.tagline')}</p>
        </div>

        <div className="mb-10 bg-base-200 rounded-xl p-4">
          <img
            src={mainImage}
            alt="Appartement Épidote - La Marmotte Joyeuse"
            className="w-full h-[400px] object-cover rounded-lg mb-4 transition-all duration-300"
          />
          <div className="flex gap-3 overflow-x-auto py-1">
            {IMAGES.map((img, i) => (
              <img
                key={i}
                src={img}
                alt={`Vue ${i + 1}`}
                onClick={() => setMainImage(img)}
                className={`w-28 h-20 object-cover rounded-lg cursor-pointer flex-shrink-0 transition-all duration-300
                  ${mainImage === img ? 'ring-2 ring-primary ring-offset-2' : 'hover:opacity-80'}`}
              />
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-10 mb-10">
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-semibold mb-3">{t('epidote.descTitle')}</h2>
              <p className="text-base-content/80 leading-relaxed">{t('epidote.desc')}</p>
            </div>

            <div>
              <h2 className="text-2xl font-semibold mb-3">{t('epidote.equipTitle')}</h2>
              <ul className="space-y-2">
                {Array.isArray(equipements) && equipements.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-base-content/80">
                    <span className="text-primary mt-1 flex-shrink-0">✓</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="space-y-4">
            <h2 className="text-2xl font-semibold">{t('epidote.infoTitle')}</h2>
            <div className="bg-base-200 p-6 rounded-xl space-y-5">
              <div>
                <h3 className="font-semibold text-sm text-primary uppercase tracking-wide">{t('epidote.capacity')}</h3>
                <p>{t('epidote.capacityVal')}</p>
              </div>
              <div>
                <h3 className="font-semibold text-sm text-primary uppercase tracking-wide">{t('epidote.surface')}</h3>
                <p>{t('epidote.surfaceVal')}</p>
              </div>
              <div>
                <h3 className="font-semibold text-sm text-primary uppercase tracking-wide">{t('epidote.rate')}</h3>
                <p className="text-2xl font-bold">{t('epidote.rateVal')} <span className="text-base font-normal text-base-content/60">{t('epidote.perNight')}</span></p>
              </div>
              <a
                href={AIRBNB_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary w-full gap-2"
              >
                {t('epidote.bookAirbnb')}
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default AppartementEpidote;
